import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/material';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';

import GradientLineChart from 'examples/Charts/LineCharts/GradientLineChart';
import HorizontalBarChart from 'examples/Charts/BarCharts/HorizontalBarChart';

import DateFilterPresets from 'components/DateFilterPresets';
import EntityFilter from 'components/EntityFilter';
import MetricCard from 'components/MetricCard';
import ClientPerformanceTable from 'components/ClientPerformanceTable';
import GamePerformanceTable from 'components/GamePerformanceTable';
import TopCountriesChart from 'components/TopCountriesChart';

import usePersistedFilters from 'hooks/usePersistedFilters';
import { exportDashboardToCsv } from 'utils/exportToCsv';

import { useNavigate } from 'react-router-dom';
import { Can } from 'context';

import {
  getStatistics,
  ggrForPeriod,
  ggrForPeriodAverage,
  getEmailSummary,
  getCountryChart,
  getGameSessions,
  getChartAmount,
  getDashboardMetrics,
  getGameSummary
} from 'services/analytics';

const COUNTRY_NAMES = {
  AF: 'Afghanistan',
  AL: 'Albania',
  DZ: 'Algeria',
  AD: 'Andorra',
  AO: 'Angola',
  AR: 'Argentina',
  AM: 'Armenia',
  AU: 'Australia',
  AT: 'Austria',
  AZ: 'Azerbaijan',
  BS: 'Bahamas',
  BH: 'Bahrain',
  BD: 'Bangladesh',
  BY: 'Belarus',
  BE: 'Belgium',
  BZ: 'Belize',
  BJ: 'Benin',
  BT: 'Bhutan',
  BO: 'Bolivia',
  BA: 'Bosnia',
  BW: 'Botswana',
  BR: 'Brazil',
  BN: 'Brunei',
  BG: 'Bulgaria',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  KH: 'Cambodia',
  CM: 'Cameroon',
  CA: 'Canada',
  CL: 'Chile',
  CN: 'China',
  CO: 'Colombia',
  CR: 'Costa Rica',
  HR: 'Croatia',
  CU: 'Cuba',
  CY: 'Cyprus',
  CZ: 'Czechia',
  DK: 'Denmark',
  DO: 'Dominican Republic',
  EC: 'Ecuador',
  EG: 'Egypt',
  SV: 'El Salvador',
  EE: 'Estonia',
  ET: 'Ethiopia',
  FI: 'Finland',
  FR: 'France',
  GE: 'Georgia',
  DE: 'Germany',
  GH: 'Ghana',
  GR: 'Greece',
  GT: 'Guatemala',
  HN: 'Honduras',
  HK: 'Hong Kong',
  HU: 'Hungary',
  IS: 'Iceland',
  IN: 'India',
  ID: 'Indonesia',
  IR: 'Iran',
  IQ: 'Iraq',
  IE: 'Ireland',
  IL: 'Israel',
  IT: 'Italy',
  JM: 'Jamaica',
  JP: 'Japan',
  JO: 'Jordan',
  KZ: 'Kazakhstan',
  KE: 'Kenya',
  KW: 'Kuwait',
  KG: 'Kyrgyzstan',
  LA: 'Laos',
  LV: 'Latvia',
  LB: 'Lebanon',
  LY: 'Libya',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MO: 'Macau',
  MK: 'North Macedonia',
  MG: 'Madagascar',
  MY: 'Malaysia',
  MV: 'Maldives',
  MT: 'Malta',
  MX: 'Mexico',
  MD: 'Moldova',
  MC: 'Monaco',
  MN: 'Mongolia',
  ME: 'Montenegro',
  MA: 'Morocco',
  MZ: 'Mozambique',
  MM: 'Myanmar',
  NA: 'Namibia',
  NP: 'Nepal',
  NL: 'Netherlands',
  NZ: 'New Zealand',
  NI: 'Nicaragua',
  NG: 'Nigeria',
  NO: 'Norway',
  OM: 'Oman',
  PK: 'Pakistan',
  PA: 'Panama',
  PY: 'Paraguay',
  PE: 'Peru',
  PH: 'Philippines',
  PL: 'Poland',
  PT: 'Portugal',
  QA: 'Qatar',
  RO: 'Romania',
  RU: 'Russia',
  SA: 'Saudi Arabia',
  SN: 'Senegal',
  RS: 'Serbia',
  SG: 'Singapore',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ZA: 'South Africa',
  KR: 'South Korea',
  ES: 'Spain',
  LK: 'Sri Lanka',
  SE: 'Sweden',
  CH: 'Switzerland',
  TW: 'Taiwan',
  TJ: 'Tajikistan',
  TZ: 'Tanzania',
  TH: 'Thailand',
  TN: 'Tunisia',
  TR: 'Turkey',
  TM: 'Turkmenistan',
  UA: 'Ukraine',
  AE: 'UAE',
  GB: 'United Kingdom',
  US: 'United States',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VE: 'Venezuela',
  VN: 'Vietnam',
  YE: 'Yemen',
  ZM: 'Zambia',
  ZW: 'Zimbabwe'
};

const getCountryName = (code) => COUNTRY_NAMES[code?.toUpperCase()] || code;

function Dashboard() {
  const navigate = useNavigate();
  const { filters, setDateRange, setEntityFilters } = usePersistedFilters();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const divider = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const panelHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#fafafa';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.05)' : '#f4f5f7';
  const tableHeaderColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const primaryText = isDark ? theme.palette.text.primary : '#0f172a';
  const secondaryText = isDark ? theme.palette.text.secondary : '#6b7280';
  const rowAlt = isDark ? 'rgba(255,255,255,0.02)' : '#fafafa';
  const rowTop = isDark ? 'rgba(226,0,27,0.06)' : 'rgba(226,0,27,0.025)';
  const rowHover = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)';
  const rowBorder = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const barTrack = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const footerBg = isDark ? 'rgba(255,255,255,0.02)' : '#fafafa';
  const footerText = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)';

  const [loading, setLoading] = useState(true);
  const [showGameIntegration, setShowGameIntegration] = useState(false);
  const [metrics, setMetrics] = useState({
    ggr: [],
    statistics: {},
    clientSummary: [],
    clientAverages: [],
    countries: [],
    countryCounts: [],
    // New dashboard metrics from API
    dashboardMetrics: null,
    gameSummary: []
  });

  const [chartData, setChartData] = useState({
    gameWins: [],
    gameLoses: [],
    months: [],
    dailyBets: []
  });

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const clientNames = filters.clientNames || [];

      // Build filter params for ALL APIs - use first ID from arrays for single-select APIs
      const apiFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        clientId: filters.clientIds?.[0] || undefined,
        operatorId: filters.operatorIds?.[0] || undefined,
        brandId: filters.brandIds?.[0] || undefined
      };

      // Add aggregateByName based on showGameIntegration toggle (false = show per-integration)
      const gameSummaryFilters = { ...apiFilters, aggregateByName: !showGameIntegration };

      const [statsRes, ggrRes, summaryRes, countryRes, sessionsRes, avgRes, dashboardRes, gameSummaryRes] =
        await Promise.all([
          getStatistics(apiFilters),
          ggrForPeriod(apiFilters),
          getEmailSummary(apiFilters),
          getCountryChart(apiFilters),
          getGameSessions(),
          ggrForPeriodAverage(apiFilters),
          getDashboardMetrics(apiFilters),
          getGameSummary(gameSummaryFilters)
        ]);

      let ggrData = Array.isArray(ggrRes?.data) ? ggrRes.data : [];
      if (clientNames.length > 0 && Array.isArray(ggrData)) {
        ggrData = ggrData.filter((g) => clientNames.includes(g.client));
      }

      const summaryData = summaryRes?.data || {};
      const clientSummary = Object.entries(summaryData).map(([name, data]) => ({
        name,
        playerCount: data?.playerCount || 0,
        betCount: data?.betCount || 0,
        betAverage: data?.betAverage || 0,
        betSum: data?.betSum || 0,
        nonRolledBackBetsCount: data?.nonRolledBackBetsCount || 0,
        nonRollbackedBetsAmount: data?.nonRollbackedBetsAmount || 0,
        percentRolledBackBets: data?.percentRolledBackBets || 0
      }));

      const countryData = countryRes?.data || [];
      const countries = [];
      const countryCounts = [];
      if (Array.isArray(countryData)) {
        countryData.forEach((e) => {
          countries.push(getCountryName(e.country));
          countryCounts.push(Number(e.country_count));
        });
      }

      const clientAverages = Array.isArray(avgRes?.data) ? avgRes.data : [];

      // Extract game summary data
      const gameSummary = gameSummaryRes?.success ? gameSummaryRes.data?.games || [] : [];

      setMetrics({
        ggr: ggrData,
        statistics: statsRes?.data || {},
        clientSummary,
        clientAverages,
        countries,
        countryCounts,
        dashboardMetrics: dashboardRes?.success ? dashboardRes.data : null,
        gameSummary
      });

      setChartData((prev) => ({
        ...prev,
        dailyBets: sessionsRes || []
      }));
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
    setLoading(false);
  }, [
    filters.startDate,
    filters.endDate,
    filters.clientNames,
    filters.clientIds,
    filters.operatorIds,
    filters.brandIds,
    showGameIntegration
  ]);

  const fetchChartData = useCallback(async () => {
    try {
      // Build filter params for chart APIs
      const chartFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        clientId: filters.clientIds?.[0] || undefined,
        operatorId: filters.operatorIds?.[0] || undefined,
        brandId: filters.brandIds?.[0] || undefined
      };

      const [winRes, loseRes] = await Promise.all([
        getChartAmount('win', chartFilters),
        getChartAmount('lose', chartFilters)
      ]);

      // Format label based on interval type
      const formatLabel = (date, interval) => {
        if (!date) return '-';
        const d = new Date(date);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        switch (interval) {
          case 'hour':
            return `${String(d.getHours()).padStart(2, '0')}:00`;
          case 'day':
            return `${months[d.getMonth()]} ${d.getDate()}`;
          case 'week':
            return `${months[d.getMonth()]} ${d.getDate()}`;
          case 'month':
          default:
            return months[d.getMonth()];
        }
      };

      // Safely extract data with null checks
      const interval = winRes?.interval || loseRes?.interval || 'month';
      const winData = winRes?.data || [];
      const loseData = loseRes?.data || [];

      // Create maps keyed by timestamp for alignment
      const toMap = (arr) =>
        arr.reduce((acc, item) => {
          if (item.date) {
            // Ensure date is a Date object (handle string dates from API)
            const dateObj = item.date instanceof Date ? item.date : new Date(item.date);
            if (!isNaN(dateObj.getTime())) {
              acc[dateObj.getTime()] = item.amount;
            }
          }
          return acc;
        }, {});

      const winMap = toMap(winData);
      const loseMap = toMap(loseData);

      // Get all unique timestamps and sort them
      const allTimestamps = Array.from(new Set([...Object.keys(winMap), ...Object.keys(loseMap)].map(Number))).sort(
        (a, b) => a - b
      );

      setChartData((prev) => ({
        ...prev,
        months: allTimestamps.map((ts) => formatLabel(new Date(ts), interval)),
        gameWins: allTimestamps.map((ts) => Number(winMap[ts] ?? 0)),
        gameLoses: allTimestamps.map((ts) => Number(loseMap[ts] ?? 0))
      }));
    } catch (err) {
      console.error('Error fetching chart data:', err);
      // Reset chart data on error to show empty state
      setChartData((prev) => ({
        ...prev,
        months: [],
        gameWins: [],
        gameLoses: []
      }));
    }
  }, [filters.startDate, filters.endDate, filters.clientIds, filters.operatorIds, filters.brandIds]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const handleDateChange = (startDate, endDate, preset) => {
    setDateRange(startDate, endDate, preset);
  };

  const handleEntityFilterChange = (entityFilters) => {
    setEntityFilters(entityFilters);
  };

  const getGgrTotal = () => {
    if (!metrics.ggr || !Array.isArray(metrics.ggr) || metrics.ggr.length === 0) return '-';
    const total = metrics.ggr.reduce((sum, g) => sum + (parseFloat(g.ggr) || 0), 0);
    return total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getGgrBreakdown = () => {
    if (!metrics.ggr || !Array.isArray(metrics.ggr) || metrics.ggr.length === 0) return null;
    return [...metrics.ggr]
      .sort((a, b) => (parseFloat(b.ggr) || 0) - (parseFloat(a.ggr) || 0))
      .map((g) => ({
        label: g.client || 'Unknown',
        value: parseFloat(g.ggr).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }));
  };

  const getTotalBetsCount = () => {
    // Use dashboard API when available (respects entity filters)
    if (metrics.dashboardMetrics?.totalBets?.count !== undefined) {
      return metrics.dashboardMetrics.totalBets.count.toLocaleString();
    }
    // Fallback to old API
    if (!metrics.clientSummary || metrics.clientSummary.length === 0) {
      return metrics.statistics?.betsForPeriod?.toLocaleString() || '-';
    }
    const total = metrics.clientSummary.reduce((sum, c) => sum + (c.betCount || 0), 0);
    return total.toLocaleString();
  };

  const getTotalBetsAmount = () => {
    // Use dashboard API when available (respects entity filters)
    if (metrics.dashboardMetrics?.totalBets?.amount !== undefined) {
      return metrics.dashboardMetrics.totalBets.amount;
    }
    // Fallback to old API
    if (!metrics.clientSummary || metrics.clientSummary.length === 0) return null;
    const total = metrics.clientSummary.reduce((sum, c) => sum + (c.betSum || 0), 0);
    return total;
  };

  const getBetsBreakdown = () => {
    if (!metrics.clientSummary || metrics.clientSummary.length === 0) {
      if (!metrics.statistics?.betsForPeriodAmount) return null;
      return metrics.statistics.betsForPeriodAmount.map((b) => ({
        label: b.currency,
        value: parseFloat(b.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }));
    }
    return metrics.clientSummary
      .filter((c) => c.betSum > 0)
      .sort((a, b) => b.betSum - a.betSum)
      .map((c) => ({
        label: c.name,
        value: parseFloat(c.betSum).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }));
  };

  const getProfit = () => {
    // Use NGR from dashboard API when entity filters are applied (NGR = rake = profit)
    if (metrics.dashboardMetrics?.ngr !== undefined) {
      return metrics.dashboardMetrics.ngr.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    // Fallback to old API when no filters applied
    if (!metrics.statistics?.profitForPeriod) return '-';
    const total = metrics.statistics.profitForPeriod.reduce((sum, p) => sum + Number(p.profit || 0), 0);
    return total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // New dashboard metrics helpers
  const getDashboardGgr = () => {
    if (metrics.dashboardMetrics?.ggr !== undefined) {
      return metrics.dashboardMetrics.ggr.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return getGgrTotal();
  };

  const getDashboardNgr = () => {
    if (metrics.dashboardMetrics?.ngr !== undefined) {
      return metrics.dashboardMetrics.ngr.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return null;
  };

  const getDashboardTotalWins = () => {
    if (metrics.dashboardMetrics?.totalWins) {
      return {
        count: metrics.dashboardMetrics.totalWins.count.toLocaleString(),
        amount: metrics.dashboardMetrics.totalWins.amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      };
    }
    return null;
  };

  const getDashboardRoundsPlayed = () => {
    if (metrics.dashboardMetrics?.roundsPlayed !== undefined) {
      return metrics.dashboardMetrics.roundsPlayed.toLocaleString();
    }
    return null;
  };

  const getDashboardAvgBetSize = () => {
    if (metrics.dashboardMetrics?.averageBetSize !== undefined) {
      return metrics.dashboardMetrics.averageBetSize.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return null;
  };

  const getDashboardRoundsPerSession = () => {
    if (metrics.dashboardMetrics?.roundsPerSession !== undefined) {
      return metrics.dashboardMetrics.roundsPerSession.toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
    }
    return null;
  };

  const getDashboardSessionLength = () => {
    if (metrics.dashboardMetrics?.averageSessionLength !== undefined) {
      const seconds = metrics.dashboardMetrics.averageSessionLength / 1000;
      if (seconds < 60) return `${Math.round(seconds)}s`;
      if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
      return `${(seconds / 3600).toFixed(1)}h`;
    }
    return null;
  };

  const getDashboardUniquePlayers = () => {
    if (metrics.dashboardMetrics?.uniquePlayers !== undefined) {
      return metrics.dashboardMetrics.uniquePlayers.toLocaleString();
    }
    return null;
  };

  const getDashboardTotalSessions = () => {
    if (metrics.dashboardMetrics?.totalSessions !== undefined) {
      return metrics.dashboardMetrics.totalSessions.toLocaleString();
    }
    return null;
  };

  const getChartDescription = () => {
    // Determine interval based on date range (same logic as getChartAmount)
    if (filters.startDate && filters.endDate) {
      const start = dayjs(filters.startDate);
      const end = dayjs(filters.endDate);
      const daysDiff = end.diff(start, 'day');

      if (daysDiff <= 1) return 'Hourly performance ($)';
      if (daysDiff <= 14) return 'Daily performance ($)';
      if (daysDiff <= 90) return 'Weekly performance ($)';
    }
    return 'Monthly performance ($)';
  };

  const gameChartData = {
    labels: chartData.months,
    datasets: [
      { label: 'Wins', data: chartData.gameWins, color: 'success' },
      { label: 'Losses', data: chartData.gameLoses, color: 'error' }
    ]
  };

  const dailyBetsData = {
    labels: ['Daily Bets'],
    datasets: [{ label: 'Bets', data: chartData.dailyBets, color: 'info' }]
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>
        {/* Header Card with Filters */}
        <Card
          sx={{
            mb: 3,
            borderRadius: '14px',
            overflow: 'hidden'
          }}
        >
          {/* Animated gradient accent bar */}
          <MDBox
            sx={{
              height: '3px',
              background: '#e2001b'
            }}
          />
          <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
            <MDBox display='flex' justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap='wrap' gap={2} mb={3}>
              <MDBox display='flex' alignItems='center' gap={{ xs: 1.5, sm: 2 }}>
                <MDBox
                  sx={{
                    width: { xs: '40px', sm: '48px' },
                    height: { xs: '40px', sm: '48px' },
                    borderRadius: '14px',
                    background: '#e2001b',
                    boxShadow: '0 4px 12px rgba(226,0,27,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Icon sx={{ color: 'white !important', fontSize: '1.5rem !important' }}>dashboard</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant='h5' fontWeight='bold' sx={{ letterSpacing: '-0.01em', lineHeight: 1.2, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                    Main Dashboard
                  </MDTypography>
                  <MDTypography variant='body2' color='text' sx={{ opacity: 0.6, fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>
                    Platform-wide metrics across all game types
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox display='flex' gap={1} flexWrap='wrap' sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <MDButton
                  variant='outlined'
                  color='success'
                  size='small'
                  onClick={() =>
                    exportDashboardToCsv('main-dashboard-export', {
                      ggr: metrics.ggr,
                      statistics: metrics.statistics,
                      filters
                    })
                  }
                  disabled={loading}
                  sx={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.72rem', flex: { xs: 1, sm: '0 0 auto' } }}
                >
                  <Icon sx={{ mr: 0.5, fontSize: '1rem !important' }}>download</Icon>
                  Export CSV
                </MDButton>
                <MDButton
                  variant='gradient'
                  color='info'
                  size='small'
                  onClick={() => navigate('/pvp-dashboard')}
                  sx={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.72rem', flex: { xs: 1, sm: '0 0 auto' } }}
                >
                  <Icon sx={{ mr: 0.5, fontSize: '1rem !important' }}>sports_esports</Icon>
                  PvP Dashboard
                </MDButton>
                <MDButton
                  variant='gradient'
                  color='warning'
                  size='small'
                  onClick={() => navigate('/slots-dashboard')}
                  sx={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.72rem', flex: { xs: 1, sm: '0 0 auto' } }}
                >
                  <Icon sx={{ mr: 0.5, fontSize: '1rem !important' }}>casino</Icon>
                  Slots Dashboard
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox
              mb={3}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${divider}`
              }}
            >
              <MDBox display='flex' alignItems='center' gap={0.75} mb={1.5}>
                <Icon sx={{ fontSize: '0.9rem !important', color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}>date_range</Icon>
                <MDTypography variant='button' fontWeight='bold' sx={{ fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                  Date Range
                </MDTypography>
              </MDBox>
              <DateFilterPresets
                onDateChange={handleDateChange}
                preset={filters.preset || 'today'}
                startDate={filters.startDate}
                endDate={filters.endDate}
              />
            </MDBox>

            <MDBox
              sx={{
                p: 2,
                borderRadius: '12px',
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${divider}`
              }}
            >
              <MDBox display='flex' alignItems='center' gap={0.75} mb={1.5}>
                <Icon sx={{ fontSize: '0.9rem !important', color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}>filter_list</Icon>
                <MDTypography variant='button' fontWeight='bold' sx={{ fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                  Entity Filters
                </MDTypography>
              </MDBox>
              <EntityFilter
                onFilterChange={handleEntityFilterChange}
                initialValues={{
                  clientIds: filters.clientIds,
                  operatorIds: filters.operatorIds,
                  brandIds: filters.brandIds
                }}
              />
            </MDBox>
          </MDBox>
        </Card>

        {/* Core Financial Metrics */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: '#e2001b', flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: primaryText, fontSize: '0.95rem' }}>
              Core Financial Metrics
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='GGR'
                value={getDashboardGgr()}
                subtitle='Gross Gaming Revenue'
                icon='attach_money'
                iconColor='success'
                loading={loading}
                breakdown={getGgrBreakdown()}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='NGR'
                value={getDashboardNgr()}
                placeholder={!getDashboardNgr()}
                placeholderText='No data'
                subtitle='Net Gaming Revenue'
                icon='trending_up'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='Profit'
                value={getProfit()}
                subtitle='Net Profit'
                icon='account_balance_wallet'
                iconColor='warning'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='Total Bets'
                value={getTotalBetsCount()}
                subtitle={
                  getTotalBetsAmount()
                    ? `$${getTotalBetsAmount().toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} turnover`
                    : 'Count'
                }
                icon='casino'
                iconColor='primary'
                loading={loading}
                breakdown={getBetsBreakdown()}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='Total Wins'
                value={getDashboardTotalWins()?.count}
                placeholder={!getDashboardTotalWins()}
                placeholderText='No data'
                subtitle={getDashboardTotalWins() ? `$${getDashboardTotalWins().amount} payout` : 'Player Winnings'}
                icon='emoji_events'
                iconColor='warning'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Activity & Engagement Metrics */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: '#e2001b', flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: primaryText, fontSize: '0.95rem' }}>
              Activity & Engagement
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Rounds Played'
                value={getDashboardRoundsPlayed()}
                placeholder={!getDashboardRoundsPlayed()}
                placeholderText='No data'
                icon='sports_esports'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Avg Bet Size'
                value={getDashboardAvgBetSize() ? `$${getDashboardAvgBetSize()}` : null}
                placeholder={!getDashboardAvgBetSize()}
                placeholderText='No data'
                icon='payments'
                iconColor='secondary'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Rounds/Session'
                value={getDashboardRoundsPerSession()}
                placeholder={!getDashboardRoundsPerSession()}
                placeholderText='No data'
                icon='repeat'
                iconColor='primary'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Session Length'
                value={getDashboardSessionLength()}
                placeholder={!getDashboardSessionLength()}
                icon='schedule'
                iconColor='info'
                placeholderText='No data'
                subtitle='Average duration'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Unique Players'
                value={getDashboardUniquePlayers()}
                placeholder={!getDashboardUniquePlayers()}
                placeholderText='No data'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Total Sessions'
                value={getDashboardTotalSessions()}
                placeholder={!getDashboardTotalSessions()}
                placeholderText='No data'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Charts Row */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: '#e2001b', flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: primaryText, fontSize: '0.95rem' }}>
              Performance Charts
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              {chartData.gameWins.length > 0 || chartData.gameLoses.length > 0 ? (
                <GradientLineChart
                  title='Wins vs Losses'
                  description={getChartDescription()}
                  chart={gameChartData}
                  height='300px'
                  hideFilters
                />
              ) : (
                <Card>
                  <MDBox
                    p={3}
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    justifyContent='center'
                    height='300px'
                  >
                    <MDTypography variant='h6' fontWeight='medium' mb={1}>
                      Wins vs Losses
                    </MDTypography>
                    <MDTypography variant='body2' color='text'>
                      No chart data available for the selected period
                    </MDTypography>
                  </MDBox>
                </Card>
              )}
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {/* Panel header */}
                <MDBox
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.75,
                    borderBottom: `1px solid ${divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: panelHeaderBg
                  }}
                >
                  <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: primaryText }}>
                    Bets by Integration
                  </MDTypography>
                  <MDBox
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: '6px',
                      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      border: `1px solid ${divider}`
                    }}
                  >
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: tableHeaderColor, letterSpacing: '0.04em' }}>
                      {filters.preset === 'today' ? 'Today' : filters.preset === 'yesterday' ? 'Yesterday' : filters.preset === 'weekly' ? 'This week' : filters.preset === 'monthly' ? 'This month' : filters.preset === 'ytd' ? 'YTD' : filters.preset === 'yearly' ? '365 days' : filters.preset === 'alltime' ? 'All time' : 'Custom'}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
                  {loading ? (
                    <MDBox mt={2}>
                      {[1, 2, 3, 4].map(i => (
                        <MDBox key={i} mb={1.5}>
                          <Skeleton variant='text' width='45%' height={16} sx={{ mb: 0.5 }} />
                          <Skeleton variant='rectangular' height={8} sx={{ borderRadius: '4px' }} />
                        </MDBox>
                      ))}
                    </MDBox>
                  ) : metrics.clientSummary.filter((c) => c.betCount > 0).length > 0 ? (
                    <MDBox mt={2}>
                      {metrics.clientSummary
                        .filter((c) => c.betCount > 0)
                        .sort((a, b) => (b.betSum || 0) - (a.betSum || 0))
                        .slice(0, 8)
                        .map((client, index, arr) => {
                          const maxVal = arr[0]?.betSum || 1;
                          const pct = Math.round(((client.betSum || 0) / maxVal) * 100);
                          const barColor = index === 0 ? '#e2001b' : `rgba(226,0,27,${0.55 - index * 0.06})`;
                          return (
                            <MDBox key={client.clientId || index} mb={1.5}>
                              <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={0.4}>
                                <MDBox display='flex' alignItems='center' gap={0.75}>
                                  <MDBox sx={{ width: '6px', height: '6px', borderRadius: '50%', background: barColor, flexShrink: 0 }} />
                                  <MDTypography variant='caption' fontWeight={index === 0 ? 'bold' : 'medium'} sx={{ fontSize: '0.75rem', color: primaryText }}>
                                    {client.name || client.clientName || `Client ${client.clientId}`}
                                  </MDTypography>
                                </MDBox>
                                <MDBox display='flex' alignItems='center' gap={0.5}>
                                  <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.7rem', color: primaryText }}>
                                    {client.betCount.toLocaleString()}
                                  </MDTypography>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.65rem', opacity: 0.5 }}>
                                    bets
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                              <MDBox sx={{ width: '100%', height: '6px', borderRadius: '3px', background: barTrack, overflow: 'hidden' }}>
                                <MDBox sx={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width 0.5s ease' }} />
                              </MDBox>
                            </MDBox>
                          );
                        })}
                    </MDBox>
                  ) : (
                    <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={4} gap={1}>
                      <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No bet data available</MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* Client Performance Table */}
        <Can I='manage' a='email_metrics'>
          <MDBox mb={3}>
            <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
              <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: '#e2001b', flexShrink: 0 }} />
              <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: primaryText, fontSize: '0.95rem' }}>
                Integration Performance
              </MDTypography>
              <MDBox sx={{ flex: 1, height: '1px', background: divider, display: { xs: 'none', sm: 'block' } }} />
            </MDBox>
            <ClientPerformanceTable data={metrics.clientSummary} loading={loading} title='Performance by Integration' />
          </MDBox>

          <MDBox mb={3}>
            <Card
              sx={{
                borderRadius: '14px',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Panel header */}
              <MDBox
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 1.75,
                  borderBottom: `1px solid ${divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: panelHeaderBg
                }}
              >
                <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: primaryText }}>
                  GGR & Average Metrics by Integration
                </MDTypography>
              </MDBox>
              <MDBox>
                {loading ? (
                  <MDBox sx={{ p: { xs: 2, sm: 3 } }}><Skeleton variant='rectangular' height={260} sx={{ borderRadius: '8px' }} /></MDBox>
                ) : metrics.clientAverages.length > 0 ? (
                  <TableContainer
                    sx={{
                      maxHeight: 460,
                      overflowX: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      '&::-webkit-scrollbar': { height: '4px', width: '4px' },
                      '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', borderRadius: '2px' }
                    }}
                  >
                    <Table size='small' stickyHeader sx={{ minWidth: { xs: 360, sm: 'auto' } }}>
                      <TableHead>
                        <TableRow
                          sx={{
                            '& th': {
                              background: tableHeaderBg,
                              color: tableHeaderColor,
                              fontWeight: 700,
                              fontSize: '0.67rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.07em',
                              padding: { xs: '9px 10px', sm: '9px 16px' },
                              borderBottom: `1px solid ${divider}`,
                              whiteSpace: 'nowrap',
                              boxShadow: 'none'
                            }
                          }}
                        >
                          <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
                          <TableCell>Integration</TableCell>
                          <TableCell align='right'>GGR</TableCell>
                          <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Avg Bet</TableCell>
                          <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Avg Rake</TableCell>
                          <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Avg Win</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metrics.clientAverages
                          .map((client) => {
                            const ggrEntry = Array.isArray(metrics.ggr) ? metrics.ggr.find((g) => g.client === client.client) : undefined;
                            return { ...client, ggr: ggrEntry?.ggr || 0 };
                          })
                          .filter((c) => c.bet > 0 || c.rake > 0 || c.win > 0 || c.ggr !== 0)
                          .sort((a, b) => (b.ggr || 0) - (a.ggr || 0))
                          .map((client, index) => {
                            const isTop = index === 0;
                            const ggrPositive = Number(client.ggr) >= 0;
                            return (
                              <TableRow
                                key={client.client || index}
                                sx={{
                                  background: isTop ? rowTop : index % 2 === 0 ? 'transparent' : rowAlt,
                                  '@media (hover: hover)': { '&:hover': { background: rowHover } },
                                  '& td': { padding: { xs: '9px 10px', sm: '9px 16px' }, borderBottom: `1px solid ${rowBorder}` },
                                  '&:last-child td': { borderBottom: 'none' }
                                }}
                              >
                                <TableCell sx={{ width: 28, pr: 0 }}>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? '#e2001b' : isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)', fontWeight: isTop ? 700 : 400 }}>
                                    {index + 1}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: primaryText }}>
                                    {client.client || 'Unknown'}
                                  </MDTypography>
                                </TableCell>
                                <TableCell align='right'>
                                  <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: ggrPositive ? '#1fa678' : '#db3f57' }}>
                                    {Number(client.ggr || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </MDTypography>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>{Number(client.bet || 0).toFixed(2)}</MDTypography>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>{Number(client.rake || 0).toFixed(2)}</MDTypography>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>{Number(client.win || 0).toFixed(2)}</MDTypography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={5} gap={1}>
                    <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No metrics available</MDTypography>
                  </MDBox>
                )}
                {metrics.clientAverages.length > 0 && (
                  <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.25, borderTop: `1px solid ${divider}`, background: footerBg }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.68rem', color: footerText }}>
                      Sorted by GGR · Green = positive, red = negative
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </MDBox>
        </Can>

        {/* Game Performance */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: '#e2001b', flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: primaryText, fontSize: '0.95rem' }}>
              Game Performance
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>
          <GamePerformanceTable
            data={metrics.gameSummary}
            loading={loading}
            title='Performance by Game'
            showIntegration={showGameIntegration}
            onToggleIntegration={setShowGameIntegration}
          />
        </MDBox>

        {/* Geographic Insights */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: '#e2001b', flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: primaryText, fontSize: '0.95rem' }}>
              Geographic Insights
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <TopCountriesChart
                countries={metrics.countries}
                counts={metrics.countryCounts}
                loading={loading}
                title='Top Countries by Players'
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <MDBox
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.75,
                    borderBottom: `1px solid ${divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: panelHeaderBg
                  }}
                >
                  <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: primaryText }}>
                    Country Distribution
                  </MDTypography>
                  {metrics.countries.length > 0 && (
                    <MDBox sx={{ display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.35, borderRadius: '6px', lineHeight: 1, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', border: `1px solid ${divider}` }}>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: tableHeaderColor, letterSpacing: '0.04em' }}>
                        Top {Math.min(metrics.countries.length, 10)}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
                <MDBox p={3}>
                  {metrics.countries.length > 0 ? (
                    <MDBox>
                      {metrics.countries
                        .map((country, i) => ({ country, count: metrics.countryCounts[i] }))
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 10)
                        .map((item, index) => {
                          const maxCount = Math.max(...metrics.countryCounts);
                          const pct = (item.count / maxCount) * 100;
                          return (
                            <MDBox key={item.country} mb={1.25}>
                              <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={0.4}>
                                <MDTypography variant='caption' fontWeight={index === 0 ? 'bold' : 'medium'} sx={{ fontSize: '0.75rem', color: primaryText }}>
                                  {getCountryName(item.country)}
                                </MDTypography>
                                <MDBox
                                  sx={{
                                    px: 0.75,
                                    py: 0.15,
                                    borderRadius: '4px',
                                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                                  }}
                                >
                                  <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.68rem', color: primaryText }}>
                                    {item.count.toLocaleString()}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                              <MDBox sx={{ width: '100%', height: '5px', borderRadius: '3px', background: barTrack, overflow: 'hidden' }}>
                                <MDBox
                                  sx={{
                                    width: `${pct}%`,
                                    height: '100%',
                                    borderRadius: '3px',
                                    background: index === 0 ? '#e2001b' : `rgba(226,0,27,${0.6 - index * 0.05})`,
                                    transition: 'width 0.5s ease'
                                  }}
                                />
                              </MDBox>
                            </MDBox>
                          );
                        })}
                    </MDBox>
                  ) : (
                    <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={4} gap={1}>
                      <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No country data available</MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* Currency Breakdown */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: '#e2001b', flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: primaryText, fontSize: '0.95rem' }}>
              Bets by Currency
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {/* Panel header */}
                <MDBox
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.75,
                    borderBottom: `1px solid ${divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: panelHeaderBg
                  }}
                >
                  <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: primaryText }}>
                    Total Bets by Currency
                  </MDTypography>
                </MDBox>
                <MDBox>
                  {loading ? (
                    <MDBox sx={{ p: { xs: 2, sm: 3 } }}><Skeleton variant='rectangular' height={200} sx={{ borderRadius: '8px' }} /></MDBox>
                  ) : metrics.statistics?.betsForPeriodAmount?.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 300, overflowX: 'auto', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { height: '4px' }, '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', borderRadius: '2px' } }}>
                      <Table size='small' stickyHeader>
                        <TableHead>
                          <TableRow
                            sx={{
                              '& th': {
                                background: tableHeaderBg,
                                color: tableHeaderColor,
                                fontWeight: 700,
                                fontSize: '0.67rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                padding: '9px 16px',
                                borderBottom: `1px solid ${divider}`,
                                boxShadow: 'none'
                              }
                            }}
                          >
                            <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
                            <TableCell>Currency</TableCell>
                            <TableCell align='right' sx={{ minWidth: 120 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {metrics.statistics.betsForPeriodAmount
                            .filter((b) => parseFloat(b.amount) > 0)
                            .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount))
                            .map((item, index) => {
                              const isTop = index === 0;
                              return (
                                <TableRow
                                  key={item.currency || index}
                                  sx={{
                                    background: isTop ? rowTop : index % 2 === 0 ? 'transparent' : rowAlt,
                                    '@media (hover: hover)': { '&:hover': { background: rowHover } },
                                    '& td': { padding: '9px 16px', borderBottom: `1px solid ${rowBorder}` },
                                    '&:last-child td': { borderBottom: 'none' }
                                  }}
                                >
                                  <TableCell sx={{ width: 28, pr: 0 }}>
                                    <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? '#e2001b' : isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)', fontWeight: isTop ? 700 : 400 }}>
                                      {index + 1}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: primaryText }}>
                                      {item.currency}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align='right' sx={{ minWidth: 120 }}>
                                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.78rem', color: primaryText }}>
                                      {parseFloat(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={5} gap={1}>
                      <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No currency data available</MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {/* Panel header */}
                <MDBox
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.75,
                    borderBottom: `1px solid ${divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: panelHeaderBg
                  }}
                >
                  <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: primaryText }}>
                    Non-Rolled Back Bets by Currency
                  </MDTypography>
                </MDBox>
                <MDBox>
                  {loading ? (
                    <MDBox sx={{ p: { xs: 2, sm: 3 } }}><Skeleton variant='rectangular' height={200} sx={{ borderRadius: '8px' }} /></MDBox>
                  ) : metrics.statistics?.nonRollbackedBetsForPeriodAmount?.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 300, overflowX: 'auto', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { height: '4px' }, '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', borderRadius: '2px' } }}>
                      <Table size='small' stickyHeader>
                        <TableHead>
                          <TableRow
                            sx={{
                              '& th': {
                                background: tableHeaderBg,
                                color: tableHeaderColor,
                                fontWeight: 700,
                                fontSize: '0.67rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                padding: '9px 16px',
                                borderBottom: `1px solid ${divider}`,
                                boxShadow: 'none'
                              }
                            }}
                          >
                            <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
                            <TableCell>Currency</TableCell>
                            <TableCell align='right' sx={{ minWidth: 120 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {metrics.statistics.nonRollbackedBetsForPeriodAmount
                            .filter((b) => parseFloat(b.amount) > 0)
                            .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount))
                            .map((item, index) => {
                              const isTop = index === 0;
                              return (
                                <TableRow
                                  key={item.currency || index}
                                  sx={{
                                    background: isTop ? rowTop : index % 2 === 0 ? 'transparent' : rowAlt,
                                    '@media (hover: hover)': { '&:hover': { background: rowHover } },
                                    '& td': { padding: '9px 16px', borderBottom: `1px solid ${rowBorder}` },
                                    '&:last-child td': { borderBottom: 'none' }
                                  }}
                                >
                                  <TableCell sx={{ width: 28, pr: 0 }}>
                                    <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? '#e2001b' : isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)', fontWeight: isTop ? 700 : 400 }}>
                                      {index + 1}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: primaryText }}>
                                      {item.currency}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align='right' sx={{ minWidth: 120 }}>
                                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.78rem', color: primaryText }}>
                                      {parseFloat(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={5} gap={1}>
                      <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No currency data available</MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
